/**
 * Utility functions for navigating to graph visualization pages
 *
 * The dashboard URLs are configured per environment and carry the Grafana
 * variable that holds the machine id, so repointing Pulse at a different
 * dashboard is a configuration change. See `buildGrafanaMachineUrl` in
 * tf-pulse-shared for the template format.
 */

import { buildGrafanaMachineUrl } from 'tf-pulse-shared';
import { logger } from './logger';

/**
 * Resolve a configured dashboard template against the given machines and open
 * it in a new tab. Logs and does nothing if the template is missing or invalid.
 */
function openDashboard(
  template: string | undefined,
  templateName: string,
  machineIds: readonly string[]
): void {
  const { url, warning, error } = buildGrafanaMachineUrl(template, machineIds);

  if (error) {
    logger.error(`${templateName}: ${error}`);
    return;
  }

  if (warning) {
    logger.warn(`${templateName}: ${warning}`);
  }

  window.open(url, '_blank', 'noopener,noreferrer');
}

/**
 * Opens the graph page for a single machine
 * @param machineId - The ID of the machine to view graphs for
 */
export function openSingleMachineGraph(machineId: string): void {
  openDashboard(
    import.meta.env.VITE_GRAPH_SINGLE_URL,
    'VITE_GRAPH_SINGLE_URL',
    [machineId]
  );
}

/**
 * Opens the graph page for multiple machines
 * @param machineIds - Array of machine IDs to view graphs for
 */
function openMultipleMachineGraphs(machineIds: string[]): void {
  if (machineIds.length === 0) {
    logger.warn('No machine IDs provided for graph navigation');
    return;
  }

  openDashboard(
    import.meta.env.VITE_GRAPH_MULTI_URL,
    'VITE_GRAPH_MULTI_URL',
    machineIds
  );
}

/**
 * Opens graph page(s) based on the number of machines selected
 * @param machineIds - Array of machine IDs (can be single or multiple)
 */
export function openMachineGraphs(machineIds: string[]): void {
  if (machineIds.length === 0) {
    logger.warn('No machine IDs provided for graph navigation');
    return;
  }

  if (machineIds.length === 1) {
    openSingleMachineGraph(machineIds[0]);
  } else {
    openMultipleMachineGraphs(machineIds);
  }
}
